import { ClickAwayListener, IconButton, Popper, Stack, styled, useTheme } from "@mui/material";
import { CaretDoubleRightIcon } from "@phosphor-icons/react/dist/csr/CaretDoubleRight";
import { ChartBarIcon } from "@phosphor-icons/react/dist/csr/ChartBar";
import { ClockCounterClockwiseIcon } from "@phosphor-icons/react/dist/csr/ClockCounterClockwise";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { EnvelopeSimpleIcon } from "@phosphor-icons/react/dist/csr/EnvelopeSimple";
import { FileArrowDownIcon } from "@phosphor-icons/react/dist/csr/FileArrowDown";
import { FileArrowUpIcon } from "@phosphor-icons/react/dist/csr/FileArrowUp";
import { FileDashedIcon } from "@phosphor-icons/react/dist/csr/FileDashed";
import { FlaskIcon } from "@phosphor-icons/react/dist/csr/Flask";
import { FlowArrowIcon } from "@phosphor-icons/react/dist/csr/FlowArrow";
import { HouseIcon } from "@phosphor-icons/react/dist/csr/House";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import { ListMagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/ListMagnifyingGlass";
import { NotepadIcon } from "@phosphor-icons/react/dist/csr/Notepad";
import { PlugIcon } from "@phosphor-icons/react/dist/csr/Plug";
import { StarIcon } from "@phosphor-icons/react/dist/csr/Star";

import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { JSX } from "react/jsx-runtime";
import Permissions from "../../constants/permissions";
import { useAccountContext } from "../../strict/app/AccountResolver";
import NotepadDownload from "../../icons/NotepadDownload.svg?react";
import NotepadUpload from "../../icons/NotepadUpload.svg?react";
import SbLogoLightSmall from "../../icons/SbLogoLightSmall.svg?react";
import ScriptIcon from "../../icons/Script.svg?react";
import TriangleNavbarCollapsed from "../../icons/TriangleNavbarCollapsed.svg?react";
import { CircleWithCount } from "../IconForFilter/IconForFilter";
import { IfPermission } from "../Permissions";
import { NavItem } from "./ExpandedNavBar";

const PopperContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.surfaceLight10.main,
  borderTopRightRadius: "4px",
  borderBottomRightRadius: "4px",
  border: `1px solid ${theme.palette.surfaceLight20.main}`,
  boxShadow: theme.mixins.shadowSurfaceLight10,
  ...theme.applyStyles("dark", {
    boxShadow: theme.mixins.shadowSurfaceDark10,
  }),
  borderLeft: "none",
  padding: "8px 0px",
  overflow: "hidden",
  marginLeft: "18px",
  gap: "8px",
}));

const IconContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected: boolean }>(({ selected, theme }) => ({
  "&:hover": {
    cursor: "pointer",
    backgroundColor: theme.palette.surfaceLight30.main,
  },
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  backgroundColor: selected ? theme.palette.surfaceLight30.main : undefined,
  borderRadius: "8px",
  width: "44px",
  minHeight: "44px",
  position: "relative",
}));
const NavItemCollapsed = ({
  icon,
  selectedIfUrlIs,
  navigateTo,
  errorCount = 0,
}: {
  icon: JSX.Element;
  selectedIfUrlIs?: string[];
  navigateTo?: string;
  errorCount?: number;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const selected = selectedIfUrlIs?.some((url) => location.pathname.includes(url)) || navigateTo === location.pathname;
  return (
    <IconContainer
      selected={selected}
      onClick={() => {
        if (navigateTo) {
          navigate(navigateTo);
        }
      }}
    >
      {icon}
      {errorCount > 0 && (
        <Stack sx={{ position: "absolute", top: "20px", left: "20px" }}>
          <CircleWithCount count={errorCount} />
        </Stack>
      )}
    </IconContainer>
  );
};

const SubItemContainer = ({
  icon,
  children,
  selectedIfUrlIs,
}: {
  icon: JSX.Element;
  children: JSX.Element[];
  selectedIfUrlIs?: string[];
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const location = useLocation();
  const selected = selectedIfUrlIs?.some((url) => location.pathname.includes(url));
  return (
    <>
      <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
        <IconContainer
          selected={Boolean(selected)}
          onClick={(event) => {
            setAnchorEl(anchorEl ? null : event.currentTarget);
          }}
        >
          {icon}
          <TriangleNavbarCollapsed
            style={{
              position: "absolute",
              top: "36px",
              left: "36px",
            }}
            width="8px"
            height="8px"
          />
        </IconContainer>
      </ClickAwayListener>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="right"
        sx={(theme) => ({ zIndex: theme.zIndex.popper })}
      >
        <PopperContainer>{children}</PopperContainer>
      </Popper>
    </>
  );
};

const navItemProps = {
  width: "auto",
  paddingLeft: "16px",
  paddingRight: "16px",
};

const CollapsedNavBar = ({
  toggleMiniNavbar,
  logoHeight,
  toggleNavbarHeight,
  suspendedKeysCount,
}: {
  toggleMiniNavbar: () => void;
  logoHeight: string;
  toggleNavbarHeight: string;
  suspendedKeysCount: number;
}) => {
  const { urlName } = useAccountContext();
  const urlPrefix = `/app/${urlName}`;
  const theme = useTheme();
  return (
    <Stack sx={{ width: "100%", alignItems: "center", justifyContent: "center" }}>
      <Stack sx={{ width: "80px", height: logoHeight, alignItems: "center", justifyContent: "center" }}>
        <SbLogoLightSmall width="48px" height="48px" data-testid="sb-light-logo-small" />
      </Stack>
      <Stack
        sx={{
          height: `calc(99vh - ${logoHeight} - ${toggleNavbarHeight})`,
          alignItems: "center",
          width: "44px",
          gap: "10px",
        }}
      >
        <NavItemCollapsed
          icon={<HouseIcon size="24px" color={theme.palette.text40.main} />}
          navigateTo={`${urlPrefix}/dashboard`}
          selectedIfUrlIs={["/dashboard", "homepage/charts"]}
        />
        <IfPermission permission={Permissions.Backfill}>
          <NavItemCollapsed
            icon={<FlowArrowIcon size="24px" color={theme.palette.text40.main} />}
            navigateTo={`${urlPrefix}/backfills/list`}
            selectedIfUrlIs={["/backfills"]}
          />
        </IfPermission>
        <SubItemContainer
          icon={<CloudArrowDownIcon size="24px" color={theme.palette.text40.main} />}
          selectedIfUrlIs={["downloads/list", "imports/dashboard", "email-ingest"]}
        >
          <NavItem
            name="Files"
            {...navItemProps}
            icon={<FileArrowDownIcon size="24px" color={theme.palette.text40.main} />}
            navigateTo={`${urlPrefix}/downloads/list`}
          />
          <NavItem
            name="Charts"
            {...navItemProps}
            icon={<ChartBarIcon size="24px" color={theme.palette.text40.main} />}
            navigateTo={`${urlPrefix}/imports/dashboard`}
          />
          <IfPermission permission={Permissions.EmailIngestV3}>
            <NavItem
              name="Email Ingest"
              {...navItemProps}
              icon={<EnvelopeSimpleIcon size="24px" color={theme.palette.text40.main} />}
              navigateTo={`${urlPrefix}/email-ingest`}
            />
          </IfPermission>
        </SubItemContainer>
        <SubItemContainer
          icon={<CloudArrowUpIcon size="24px" color={theme.palette.text40.main} />}
          selectedIfUrlIs={["uploads/list", "exports/dashboard"]}
        >
          <NavItem
            name="Uploads"
            {...navItemProps}
            icon={<FileArrowUpIcon size="24px" color={theme.palette.text40.main} />}
            navigateTo={`${urlPrefix}/uploads/list`}
          />
          <NavItem
            name="Charts"
            {...navItemProps}
            icon={<ChartBarIcon size="24px" color={theme.palette.text40.main} />}
            navigateTo={`${urlPrefix}/exports/dashboard`}
          />
        </SubItemContainer>
        <NavItemCollapsed
          icon={<ListMagnifyingGlassIcon size="24px" color={theme.palette.text40.main} />}
          navigateTo={`${urlPrefix}/events`}
        />
        <NavItemCollapsed
          icon={<KeyIcon size="24px" color={theme.palette.text40.main} />}
          navigateTo={`${urlPrefix}/keys`}
          errorCount={suspendedKeysCount}
          selectedIfUrlIs={["keys", "keys/details"]}
        />

        <SubItemContainer
          icon={<ScriptIcon height="24px" width="24px" color={theme.palette.text40.main} />}
          selectedIfUrlIs={["scripts/published", "scripts/drafts", "scripts/tests", "scripts/history"]}
        >
          <NavItem
            name="Published"
            {...navItemProps}
            navigateTo={`${urlPrefix}/scripts/published`}
            icon={<StarIcon size="24px" color={theme.palette.text40.main} />}
          />
          <NavItem
            name="Drafts"
            {...navItemProps}
            navigateTo={`${urlPrefix}/scripts/drafts`}
            icon={<FileDashedIcon size="24px" color={theme.palette.text40.main} />}
          />
          <NavItem
            name="Tests"
            {...navItemProps}
            navigateTo={`${urlPrefix}/scripts/tests`}
            icon={<FlaskIcon size="24px" color={theme.palette.text40.main} />}
          />
          <NavItem
            name="History"
            {...navItemProps}
            navigateTo={`${urlPrefix}/scripts/history`}
            icon={<ClockCounterClockwiseIcon size="24px" color={theme.palette.text40.main} />}
          />
        </SubItemContainer>
        <NavItemCollapsed
          icon={<PlugIcon size="24px" color={theme.palette.text40.main} />}
          navigateTo={`${urlPrefix}/integrations`}
        />
        <SubItemContainer icon={<NotepadIcon size="24px" color={theme.palette.text40.main} />}>
          <NavItem
            name="Source"
            {...navItemProps}
            navigateTo={`${urlPrefix}/reports/source`}
            icon={<NotepadDownload width="24px" height="24px" color={theme.palette.text40.main} />}
          />
          <NavItem
            name="Schema"
            {...navItemProps}
            navigateTo={`${urlPrefix}/reports/schema`}
            icon={<NotepadUpload width="24px" height="24px" color={theme.palette.text40.main} />}
          />
        </SubItemContainer>
      </Stack>
      <Stack
        direction="row"
        sx={{ height: toggleNavbarHeight, justifyContent: "flex-end", alignItems: "center", padding: "16px" }}
      >
        <IconButton onClick={toggleMiniNavbar} data-testid="navbar-toggle">
          <CaretDoubleRightIcon size="24px" color={theme.palette.text40.main} data-testid="open-navbar" />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export { CollapsedNavBar };
